import { ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as API from "../@api/auth";
import {
  AuthType,
  ContextValueType,
  createPasswordType,
  LoginType,
  RegisterType,
  ResetPasswordType,
} from "./types";
import { Context } from "./useAuth";

const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const { pathname } = useLocation();
  const [auth, setAuth] = useState<AuthType | null>(null);
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      setResponse(null);
    };
  }, [pathname]);

  const initiateRequest = () => {
    setLoading(true);
    setResponse(null);
  };

  const handleError = () => {
    localStorage.removeItem("user_access_token");
    setAuth(null);
    setLoading(false);
  };

  const userAuth = useCallback(() => {
    try {
      setLoading(true);

      API.userAuth()
        .then((res: any) => {
          setAuth((prev: AuthType | null) => ({
            ...(prev ?? {}),
            user: res?.data,
            token: localStorage.getItem("user_access_token"),
          }));
        })
        .catch((error) => {
          console.error("Auth error:", error);
          handleError();
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      console.error("Auth error:", error);
      handleError();
      throw error;
    }
  }, []);

  useEffect(() => {
    userAuth();
  }, [userAuth]);

  const register = async (payload: RegisterType) => {
    try {
      initiateRequest();
      const res: any = await API.register(payload);
      setResponse(res);
      setAuth((prev: AuthType | null) => ({
        ...(prev ?? {}),
        user: res?.data,
        token: res?.meta?.token,
      }));
      localStorage.setItem("user_access_token", res?.meta?.token);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("register failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const login = async (payload: LoginType) => {
    try {
      initiateRequest();
      const res: any = await API.login(payload);
      setResponse(res);
      setAuth((prev: AuthType | null) => ({
        ...(prev ?? {}),
        user: res?.data,
        token: res?.meta?.token,
      }));
      localStorage.setItem("user_access_token", res?.meta?.token);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("login failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const googleAuth = async () => {
    try {
      initiateRequest();
      const res: any = await API.googleAuth();
      setResponse(res);
      setAuth((prev: AuthType | null) => ({
        ...(prev ?? {}),
        user: res?.data,
        token: res?.meta?.token,
      }));
      localStorage.setItem("user_access_token", res?.meta?.token);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("googleAuth failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const facebookAuth = async () => {
    try {
      initiateRequest();
      const res: any = await API.facebookAuth();
      setResponse(res);
      setAuth((prev: AuthType | null) => ({
        ...(prev ?? {}),
        user: res?.data,
        token: res?.meta?.token,
      }));
      localStorage.setItem("user_access_token", res?.meta?.token);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("googleAuth failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const linkedinAuth = async () => {
    try {
      initiateRequest();
      const res: any = await API.linkedinAuth();
      setResponse(res);
      setAuth((prev: AuthType | null) => ({
        ...(prev ?? {}),
        user: res?.data,
        token: res?.meta?.token,
      }));
      localStorage.setItem("user_access_token", res?.meta?.token);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("googleAuth failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const resetPassword = async (payload: ResetPasswordType) => {
    try {
      initiateRequest();
      const res: any = await API.resetPassword(payload);
      setResponse(res);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("reset password failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const createPassword = async (payload: createPasswordType) => {
    try {
      initiateRequest();
      const res: any = await API.createPassword(payload);
      setResponse(res);
      setLoading(false);
      return res;
    } catch (error) {
      console.error("create password failed:", error);
      setResponse(error);
      handleError();
      throw error;
    }
  };

  const logout = () => {
    try {
      //TODO
      //add logout API
      localStorage.removeItem("user_access_token");
      setAuth(null);
      setResponse(null);
      setLoading(false);
    } catch (error) {
      console.error("logout error:", error);
      localStorage.removeItem("user_access_token");
    }
  };

  const contextValue: ContextValueType = {
    auth,
    loading,
    response,
    setResponse,
    setAuth,
    register,
    login,
    googleAuth,
    facebookAuth,
    linkedinAuth,
    resetPassword,
    createPassword,
    logout,
  };

  return (
    <Context.Provider value={{ ...contextValue }}>{children}</Context.Provider>
  );
};

export default AuthProvider;
